import React, { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    Stack,
    Typography,
} from '@mui/material'
import {
    ArrowBack as BackIcon,
    CalendarMonth as CalendarIcon,
} from '@mui/icons-material'

import heroWhy from '../assets/blog/why-mojocash-hero.png'
import heroNoMiddle from '../assets/blog/no-middleman.jpg'

const ORANGE = '#FF8A00'

const BlogPost = () => {
    const { slug } = useParams()
    const navigate = useNavigate()
    const { lang } = useLanguage()
    const isFr = lang === 'fr'

    const posts = useMemo(
        () => ({
            'why-mojopayr-app': {
                category: 'PRODUCT',
                title: isFr
                    ? 'Pourquoi MojoPayr est concu pour les vrais paiements au pays'
                    : 'Why MojoPayr is built for real back-home payments',
                date: 'Mar 29, 2026',
                hero: heroWhy,
                body: isFr
                    ? [
                        'MojoPayr n est pas simplement une autre application d argent. Elle se construit autour de la vraie maniere dont les utilisateurs diaspora et les communautes locales se soutiennent a travers les marches africains.',
                        'Cela veut dire qu envoyer de l argent n est pas traite comme une tache isolee. Cela se relie au paiement d un commerce, au reglement d une facture, au financement d un besoin familial, et a la conservation des preuves au meme endroit.',
                        'Pour les utilisateurs diaspora, la tarification doit sembler familiere comme dans une application de transfert : envoyer depuis votre devise de residence, voir clairement la conversion, et savoir exactement ce que recoit le beneficiaire ou le prestataire.',
                        'Pour les utilisateurs locaux, l experience doit rester plus simple et moins chere. Le but n est pas seulement d exister comme une autre option, mais de devenir une alternative pratique que les gens utilisent chaque jour.',
                        'C est pourquoi MojoPayr met l accent sur des rails directs, moins de friction, et de meilleures preuves. Dans beaucoup de marches, cette couche de confiance compte autant que le paiement lui-meme.',
                    ]
                    : [
                        'MojoPayr is not just another money app. It is being shaped around the real way diaspora users and local communities support one another across African markets.',
                        'That means sending money is not treated as an isolated task. It connects to paying a business, settling a bill, funding a family need, and keeping proof in one place.',
                        'For diaspora users, pricing should feel familiar to a transfer app: send from your resident currency, see the conversion clearly, and know exactly what the receiver or service provider gets.',
                        'For local users, the experience should stay simpler and cheaper. The goal is not just to exist as another option, but to become a practical alternative people use every day.',
                        'This is why MojoPayr focuses on direct rails, lower friction, and better proof. In many markets, that trust layer matters as much as the payment itself.',
                    ],
            },
            'no-more-middle-person-payments': {
                category: 'PAYMENTS',
                title: isFr
                    ? 'Fini les intermediaires pour les services et le soutien familial'
                    : 'No more middle person for services and family support',
                date: 'Mar 12, 2026',
                hero: heroNoMiddle,
                body: isFr
                    ? [
                        'L une des plus grandes difficultes du soutien transfrontalier est de devoir envoyer de l argent a une personne puis esperer qu elle paie la bonne ecole, le bon fournisseur, le bon commerce, ou le bon prestataire.',
                        'MojoPayr est concu pour reduire cette friction en aidant les utilisateurs a payer plus directement. Au lieu de dependre d un intermediaire, la plateforme peut rapprocher la valeur du vrai commerce ou du vrai objectif.',
                        'Cela rend les paiements plus clairs pour l expediteur, plus surs pour la famille, et plus faciles a suivre quand la preuve devient importante plus tard.',
                        'La meme logique renforce les factures, paiements marchands, taxes, factures clients, et autres parcours de service. Plus le chemin est simple, plus le produit devient digne de confiance.',
                        'C est pourquoi le comportement de paiement direct est l une des parties les plus importantes de l experience MojoPayr.',
                    ]
                    : [
                        'One of the biggest pain points in cross-border support is having to send money to a person and then hope they pay the right school, biller, shop, or service provider.',
                        'MojoPayr is built to reduce that friction by helping users pay more directly. Instead of relying on a middle person, the platform can route value closer to the actual business or purpose.',
                        'This makes payments clearer for the sender, safer for the family, and easier to track when proof matters later.',
                        'The same logic strengthens bills, merchant payments, taxes, invoices, and other service flows. The simpler the path, the more trusted the product becomes.',
                        'That is why direct payment behavior is one of the most important parts of the MojoPayr experience.',
                    ],
            },
        }),
        [isFr]
    )

    const post = posts[slug]

    if (!post) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#09061B', py: { xs: 4, md: 6 } }}>
                <Container maxWidth="md">
                    <Typography sx={{ color: '#fff', fontWeight: 900, mb: 1 }}>
                        {isFr ? 'Article introuvable' : 'Article not found'}
                    </Typography>
                    <Button onClick={() => navigate('/blog')} variant="contained" startIcon={<BackIcon />} sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 900, bgcolor: ORANGE }}>
                        {isFr ? 'Retour au blog' : 'Back to Blog'}
                    </Button>
                </Container>
            </Box>
        )
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#09061B', py: { xs: 4, md: 6 } }}>
            <Container maxWidth="md">
                <Button onClick={() => navigate('/blog')} startIcon={<BackIcon />} sx={{ mb: 2.2, borderRadius: 999, textTransform: 'none', fontWeight: 900, color: '#fff', border: '1px solid rgba(255,255,255,0.12)' }}>
                    {isFr ? 'Retour au blog' : 'Back to Blog'}
                </Button>

                <Card sx={{ borderRadius: 6, overflow: 'hidden', background: 'rgba(16, 11, 38, 0.92)', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 24px 60px rgba(0,0,0,0.22)' }}>
                    <Box role="img" aria-label={`${post.title} article cover`} sx={{ height: { xs: 240, md: 360 }, backgroundImage: `linear-gradient(180deg, rgba(9,6,27,0.08), rgba(9,6,27,0.36)), url(${post.hero})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1.2 }}>
                            <Chip label={post.category} size="small" sx={{ bgcolor: 'rgba(255,138,0,0.12)', color: ORANGE, fontWeight: 800 }} />
                            <Stack direction="row" spacing={0.8} alignItems="center" sx={{ color: 'rgba(255,255,255,0.62)' }}>
                                <CalendarIcon sx={{ fontSize: 18 }} />
                                <Typography variant="body2">{post.date}</Typography>
                            </Stack>
                        </Stack>

                        <Typography component="h1" sx={{ color: '#fff', fontWeight: 950, fontSize: { xs: 30, md: 44 }, lineHeight: 1.06 }}>
                            {post.title}
                        </Typography>

                        <Divider sx={{ my: 2.2, borderColor: 'rgba(255,255,255,0.08)' }} />

                        <Stack spacing={1.5}>
                            {post.body.map((paragraph) => (
                                <Typography key={paragraph} sx={{ color: 'rgba(255,255,255,0.78)', fontSize: { xs: 15, md: 16 }, lineHeight: 1.9 }}>
                                    {paragraph}
                                </Typography>
                            ))}
                        </Stack>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    )
}

export default BlogPost

